import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { CopilotAskSurfaceRegistry } from '@pages/copilot/ask/copilot-ask-surface.registry';
import { COPILOT_ASK_PANEL_DIALOG_CONFIG } from '@pages/copilot/ask/panel/copilot-ask-panel.config';
import { CopilotAskPanelFactory } from '@pages/copilot/ask/panel/copilot-ask-panel.factory';
import { BaseObject } from '@shared/base/base-object';
import { Authorities } from '@shared/constants/authorities';
import { AuthService } from '@shared/helpers/auth.service';
import { UserState } from '@shared/states/user.state';
import { DialogService } from '@ui/dialog/dialog.service';

/**
 * Global Copilot Ask entry (.project#1877): a floating button on every page of
 * the layout that opens the Ask panel in a right-side dialog.
 *
 * Shown only to a logged-in user who HOLDS `AI_ASSISTANT_ASK`. Unlike the navbar
 * item it does not follow the subscription upsell path (disabled authority):
 * the panel calls the Ask API directly, which would only answer 403 there.
 * Hidden while the panel is open, and while an Ask surface is already on screen
 * (the /copilot Chat tab) — hide-only: that signal is ANDed into the rule above
 * and can never show the button to someone the rule hides.
 */
@Component({
  selector: 'app-copilot-ask-launcher',
  templateUrl: './copilot-ask-launcher.component.html',
  styleUrls: ['./copilot-ask-launcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CopilotAskLauncherComponent extends BaseObject {
  public readonly _visible$: Observable<boolean>;

  private readonly panelOpen$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly authService: AuthService,
    private readonly userState: UserState,
    private readonly dialogService: DialogService,
    private readonly askSurfaces: CopilotAskSurfaceRegistry,
  ) {
    super();

    this._visible$ = combineLatest([
      this.authService.loggedIn$,
      this.userState.authorities$,
      this.panelOpen$,
      this.askSurfaces.inlineAskMounted$,
    ]).pipe(
      map(
        ([loggedIn, , panelOpen, inlineAskMounted]) =>
          loggedIn &&
          !panelOpen &&
          !inlineAskMounted &&
          this.userState.hasAuthorities([Authorities.AiAssistantAsk]),
      ),
      distinctUntilChanged(),
    );
  }

  public _open(): void {
    if (this.panelOpen$.value) {
      return;
    }

    this.panelOpen$.next(true);

    // `closed$` also fires when the panel closes on navigation or after a
    // failed chunk load (the dialog then shows its own error state), so the
    // button always comes back.
    this.dialogService
      .openLazy({ factory: CopilotAskPanelFactory }, undefined, COPILOT_ASK_PANEL_DIALOG_CONFIG)
      .closed$.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.panelOpen$.next(false));
  }
}
